import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAgents } from '../../contexts/AgentContext';
import { useSettings } from '../../contexts/SettingsContext';
import toast from 'react-hot-toast';
import SafeIcon from '../../common/SafeIcon';
import { initiateThoughtlyCall } from '../../lib/thoughtly';
import { FiAlertTriangle, FiCheck, FiClock, FiDollarSign, FiLoader, FiMessageSquare, FiMic, FiPhone, FiShield, FiUser, FiX } from 'react-icons/fi';


const AICallModal = ({ contact, onClose, onCallInitiated }) => {
  const { agents } = useAgents();
  const { settings } = useSettings();
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [customVariables, setCustomVariables] = useState({
    currentCompany: contact?.company || '',
    yearsExperience: '',
    specialties: ''
  });
  const [consentGiven, setConsentGiven] = useState(false);
  const [isInitiating, setIsInitiating] = useState(false);

  // Get suggested agent based on recruitment stage
  const getSuggestedAgent = () => {
    const stage = contact?.recruitmentStage || 'lead';
    const activeAgents = agents.filter(agent => agent.status === 'active');
    
    const stageMapping = {
      'lead': 'initial_outreach',
      'contacted': 'follow_up',
      'interested': 'follow_up',
      'nurturing': 'reactivation',
      'meeting_scheduled': 'meeting_reminder'
    };
    
    const suggestedScenario = stageMapping[stage] || 'initial_outreach';
    return activeAgents.find(agent => agent.scenario === suggestedScenario) || activeAgents[0];
  };

  useEffect(() => {
    const suggested = getSuggestedAgent();
    if (suggested) {
      setSelectedAgent(suggested);
    }
  }, [contact, agents]);

  const estimatedCost = selectedAgent ? '$0.25' : '$0.00';
  const estimatedDuration = selectedAgent ? '3-5 minutes' : 'Unknown';

  const handleInitiateCall = async () => {
    if (!selectedAgent) {
      toast.error('Please select an AI agent');
      return;
    }

    if (!consentGiven) {
      toast.error('Please confirm consent to make this call');
      return;
    }

    if (!settings.thoughtlyApiKey || !settings.thoughtlyTeamId) {
      toast.error('Thoughtly API credentials not configured');
      return;
    }

    setIsInitiating(true);

    try {
      const response = await initiateThoughtlyCall({
        contact,
        agent: selectedAgent,
        customVariables,
        overrides: {
          apiKey: settings.thoughtlyApiKey,
          teamId: settings.thoughtlyTeamId
        }
      });

      const responseId =
        response?.data?.response_id ||
        response?.data?.id ||
        response?.response_id ||
        response?.responseId ||
        response?.call?.response_id ||
        response?.call?.id;

      const callId =
        response?.call_id ||
        response?.callId ||
        response?.data?.call_id ||
        response?.data?.callId ||
        response?.id ||
        Date.now().toString();

      const callData = {
        id: callId,
        agentId: selectedAgent.id,
        agentName: selectedAgent.name,
        contactId: contact.id,
        phoneNumber: contact.phone,
        customVariables,
        initiatedAt: new Date().toISOString(),
        provider: 'thoughtly',
        providerResponse: response,
        responseId,
        callId
      };

      onCallInitiated(callData);
      toast.success('AI call initiated successfully');
      onClose();
    } catch (error) {
      console.error('Failed to initiate Thoughtly call:', error);
      toast.error(error.message || 'Failed to initiate AI call');
    } finally {
      setIsInitiating(false);
    }
  };

  const validatePhoneNumber = (phone) => {
    const phoneRegex = /^\+?[\d\s\-()]{10,}$/;
    return phoneRegex.test(phone);
  };

  const isValidCall = contact?.phone && validatePhoneNumber(contact.phone) && selectedAgent && consentGiven;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-blue-500 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiMic} className="text-white text-lg" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Initiate AI Call</h2>
              <p className="text-sm text-gray-600">
                Call {contact?.firstName} {contact?.lastName} using Thoughtly AI
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <SafeIcon icon={FiX} className="text-gray-500" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Contact Information */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-medium text-gray-900 mb-3">Contact Details</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Name:</span>
                <span className="ml-2 font-medium">{contact?.firstName} {contact?.lastName}</span>
              </div>
              <div>
                <span className="text-gray-500">Phone:</span>
                <span className="ml-2 font-medium">{contact?.phone}</span>
                {contact?.phone && !validatePhoneNumber(contact.phone) && (
                  <span className="ml-2 text-red-600 text-xs">Invalid format</span>
                )}
              </div>
              <div>
                <span className="text-gray-500">Company:</span>
                <span className="ml-2 font-medium">{contact?.company || 'Not specified'}</span>
              </div>
              <div>
                <span className="text-gray-500">Stage:</span>
                <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium capitalize">
                  {contact?.recruitmentStage?.replace('_', ' ') || 'Lead'}
                </span>
              </div>
            </div>
          </div>

          {/* Agent Selection */}
          <div>
            <h3 className="font-medium text-gray-900 mb-3">Select AI Agent</h3>
            
            {/* Suggested Agent */}
            {getSuggestedAgent() && (
              <div className="mb-4 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="font-medium text-purple-900 mb-1">Recommended Agent</h4>
                    <p className="text-sm text-purple-700 mb-2">
                      Based on {contact?.firstName}'s recruitment stage: {contact?.recruitmentStage?.replace('_', ' ') || 'Lead'}
                    </p>
                    <div className="flex items-center gap-4 text-sm">
                      <span className="font-medium">{getSuggestedAgent().name}</span>
                      <span className="text-purple-600 capitalize">{getSuggestedAgent().voice} voice</span>
                    </div>
                    <p className="text-xs text-purple-600 mt-1">{getSuggestedAgent().description}</p>
                  </div>
                  <button
                    onClick={() => setSelectedAgent(getSuggestedAgent())}
                    className={`px-4 py-2 text-sm rounded-lg transition-colors ${
                      selectedAgent?.id === getSuggestedAgent().id
                        ? 'bg-purple-600 text-white'
                        : 'bg-white border border-purple-300 text-purple-600 hover:bg-purple-50'
                    }`}
                  >
                    {selectedAgent?.id === getSuggestedAgent().id ? 'Selected' : 'Use This'}
                  </button>
                </div>
              </div>
            )}

            {/* All Available Agents */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">All Available Agents</h4>
              <div className="space-y-2">
                {agents.filter(agent => agent.status === 'active').map(agent => (
                  <div
                    key={agent.id}
                    className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                      selectedAgent?.id === agent.id
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => setSelectedAgent(agent)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-gray-900">{agent.name}</span>
                          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded capitalize">
                            {agent.voice}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600">{agent.description}</p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                          <span>Success rate: {agent.stats.successRate}%</span>
                          <span>Avg duration: {agent.stats.avgDuration}</span>
                        </div>
                      </div>
                      <input
                        type="radio"
                        checked={selectedAgent?.id === agent.id}
                        onChange={() => setSelectedAgent(agent)}
                        className="text-purple-600 focus:ring-purple-500"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Custom Variables */}
          <div>
            <h3 className="font-medium text-gray-900 mb-3">Personalization Variables</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Current Company
                </label>
                <input
                  type="text"
                  value={customVariables.currentCompany}
                  onChange={(e) => setCustomVariables(prev => ({ ...prev, currentCompany: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                  placeholder="e.g., State Farm Insurance"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Years of Experience
                </label>
                <input
                  type="text"
                  value={customVariables.yearsExperience}
                  onChange={(e) => setCustomVariables(prev => ({ ...prev, yearsExperience: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                  placeholder="e.g., 5 years"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Specialties/Notes
                </label>
                <input
                  type="text"
                  value={customVariables.specialties}
                  onChange={(e) => setCustomVariables(prev => ({ ...prev, specialties: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                  placeholder="e.g., Life insurance specialist, top performer"
                />
              </div>
            </div>
          </div>

          {/* Call Estimate */}
          <div className="bg-blue-50 rounded-lg p-4">
            <h3 className="font-medium text-blue-900 mb-3">Call Estimate</h3>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div className="flex items-center gap-2">
                <SafeIcon icon={FiDollarSign} className="text-blue-600" />
                <div>
                  <p className="font-medium text-blue-900">Estimated Cost</p>
                  <p className="text-blue-700">{estimatedCost}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <SafeIcon icon={FiClock} className="text-blue-600" />
                <div>
                  <p className="font-medium text-blue-900">Duration</p>
                  <p className="text-blue-700">{estimatedDuration}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <SafeIcon icon={FiUser} className="text-blue-600" />
                <div>
                  <p className="font-medium text-blue-900">Agent Voice</p>
                  <p className="text-blue-700 capitalize">{selectedAgent?.voice || 'Not selected'}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Compliance Section */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <SafeIcon icon={FiShield} className="text-yellow-600 mt-0.5" />
              <div className="flex-1">
                <h4 className="font-medium text-yellow-900 mb-2">Compliance & Consent</h4>
                <div className="space-y-2">
                  <label className="flex items-start gap-2">
                    <input
                      type="checkbox"
                      checked={consentGiven}
                      onChange={(e) => setConsentGiven(e.target.checked)}
                      className="mt-0.5 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                    />
                    <span className="text-sm text-yellow-800">
                      I confirm this contact has provided consent to receive recruitment calls, 
                      and this call complies with TCPA regulations and company policies.
                    </span>
                  </label>
                </div>
                <p className="text-xs text-yellow-700 mt-2">
                  This call will be recorded for quality assurance and training purposes.
                </p>
              </div>
            </div>
          </div>

          {/* Validation Warnings */}
          {!validatePhoneNumber(contact?.phone) && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="flex items-center gap-2">
                <SafeIcon icon={FiAlertTriangle} className="text-red-600" />
                <span className="text-sm font-medium text-red-900">Invalid Phone Number</span>
              </div>
              <p className="text-sm text-red-700 mt-1">
                The phone number format appears to be invalid. Please verify before proceeding.
              </p>
            </div>
          )}

          {!settings.thoughtlyApiKey && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="flex items-center gap-2">
                <SafeIcon icon={FiAlertTriangle} className="text-red-600" />
                <span className="text-sm font-medium text-red-900">API Not Configured</span>
              </div>
              <p className="text-sm text-red-700 mt-1">
                Thoughtly API credentials are not configured. Please check your settings.
              </p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleInitiateCall}
            disabled={!isValidCall || isInitiating}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isInitiating ? (
              <>
                <SafeIcon icon={FiLoader} className="animate-spin text-sm" />
                Initiating Call...
              </>
            ) : (
              <>
                <SafeIcon icon={FiPhone} className="text-sm" />
                Initiate AI Call
              </>
            )}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default AICallModal;